import { useState } from 'react';
import {
  Plus, Sparkles, Globe, FolderOpen, Trash2, X, Link2, Star,
} from 'lucide-react';
import {
  useAgentSkills,
  useInstallAgentSkill,
  useDeleteAgentSkill,
} from '../hooks/useAgentSkills';
import {
  AgentSkillScope,
  type AgentSkill,
  type InstallAgentSkillDto,
} from '../types/agent-skill';

type InstallMode = 'paste' | 'url';

function SkillCard({
  skill,
  onDelete,
}: {
  skill: AgentSkill;
  onDelete: (id: string) => void;
}) {
  const ScopeIcon = skill.scope === AgentSkillScope.PROJECT ? FolderOpen : Globe;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 transition-colors hover:border-slate-300">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-violet-50 p-2 text-violet-600">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <div className="text-sm font-medium text-slate-900">{skill.name}</div>
            <div className="mt-0.5 font-mono text-[11px] text-slate-400">
              .claude/skills/{skill.slug}
            </div>
            {skill.description && (
              <p className="mt-1 text-xs text-slate-500 line-clamp-2">
                {skill.description}
              </p>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                <ScopeIcon className="h-3 w-3" />
                {skill.scope === AgentSkillScope.PROJECT ? 'Project' : 'Global'}
              </span>
              {skill.version && (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500">
                  v{skill.version}
                </span>
              )}
              {skill.enabledByDefault && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-600">
                  <Star className="h-2.5 w-2.5" />
                  Default
                </span>
              )}
              {skill.sourceUrl && (
                <a
                  href={skill.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[10px] text-slate-400 hover:text-slate-600"
                >
                  <Link2 className="h-2.5 w-2.5" />
                  source
                </a>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={() => onDelete(skill.id)}
          className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
          title="Uninstall skill"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function AgentSkills() {
  const { data: skills, isLoading } = useAgentSkills();
  const installSkill = useInstallAgentSkill();
  const deleteSkill = useDeleteAgentSkill();

  const [showForm, setShowForm] = useState(false);
  const [mode, setMode] = useState<InstallMode>('paste');
  const [skillMd, setSkillMd] = useState('');
  const [url, setUrl] = useState('');
  const [enabledByDefault, setEnabledByDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setSkillMd('');
    setUrl('');
    setEnabledByDefault(false);
    setError(null);
    setShowForm(false);
  };

  const handleInstall = async () => {
    setError(null);
    const dto: InstallAgentSkillDto = { enabledByDefault };
    if (mode === 'paste') {
      if (!skillMd.trim()) return;
      dto.skillMd = skillMd;
    } else {
      if (!url.trim()) return;
      dto.url = url.trim();
    }
    try {
      await installSkill.mutateAsync(dto);
      reset();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string | string[] } } })
          ?.response?.data?.message ?? 'Failed to install skill';
      setError(Array.isArray(msg) ? msg.join(', ') : String(msg));
    }
  };

  return (
    <div className="mx-auto max-w-5xl p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Agent Skills</h1>
          <p className="text-sm text-slate-500">
            Install SKILL.md bundles and bind them to projects and instances
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
        >
          <Plus className="h-4 w-4" />
          Install Skill
        </button>
      </div>

      {showForm && (
        <div className="mb-4 rounded-lg border border-slate-200 bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-medium text-slate-900">Install Skill</h3>
            <button
              onClick={reset}
              className="text-slate-400 hover:text-slate-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="mb-3 flex gap-2">
            {(['paste', 'url'] as InstallMode[]).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  mode === m
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {m === 'paste' ? 'Paste SKILL.md' : 'From URL'}
              </button>
            ))}
          </div>

          {mode === 'paste' ? (
            <textarea
              placeholder={'---\nname: my-skill\ndescription: What it does\n---\n\n# Instructions...'}
              value={skillMd}
              onChange={(e) => setSkillMd(e.target.value)}
              rows={10}
              className="w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-xs focus:border-slate-500 focus:outline-none"
            />
          ) : (
            <input
              type="url"
              placeholder="https://example.com/skills/my-skill/SKILL.md"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
            />
          )}

          <label className="mt-3 flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={enabledByDefault}
              onChange={(e) => setEnabledByDefault(e.target.checked)}
              className="rounded border-slate-300"
            />
            Enable by default on all my projects
          </label>

          {error && (
            <p className="mt-2 text-xs text-red-600">{error}</p>
          )}

          <div className="mt-3 flex justify-end">
            <button
              onClick={handleInstall}
              disabled={
                installSkill.isPending ||
                (mode === 'paste' ? !skillMd.trim() : !url.trim())
              }
              className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {installSkill.isPending ? 'Installing...' : 'Install'}
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="py-12 text-center text-sm text-slate-400">
          Loading skills...
        </div>
      ) : !skills || skills.length === 0 ? (
        <div className="py-12 text-center">
          <Sparkles className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-2 text-sm text-slate-500">No skills installed</p>
          <p className="text-xs text-slate-400">
            Install a SKILL.md bundle to get started
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {skills.map((skill) => (
            <SkillCard
              key={skill.id}
              skill={skill}
              onDelete={(id) => deleteSkill.mutate(id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
